import {Component, Inject, inject} from '@angular/core';
import {DateTime} from "luxon";
import {toLicenceDate} from "../../../shared/licence-date";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {DialogBaseComponent} from "../../dialogs/dialog-base/dialog-base.component";
import {MatButton} from "@angular/material/button";
import {MatDatepicker, MatDatepickerInput, MatDatepickerToggle} from "@angular/material/datepicker";
import {MatError, MatFormField, MatHint, MatLabel, MatSuffix} from "@angular/material/form-field";
import {MatInput} from "@angular/material/input";
import {DatePipe, NgIf} from "@angular/common";
import {FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators} from "@angular/forms";
import {UIStateEnum} from "../../../enum/UIStateEnum";
import {SnackbarService} from "../../../services/snackbar.service";
import {MAT_DIALOG_DATA, MatDialogRef} from "@angular/material/dialog";
import {IGameLotteryLicence} from "../../../interfaces/IGameLotteryLicence";
import {MatOption, MatSelect} from "@angular/material/select";
import {IApiErrorResponse} from "../../../interfaces/error/IApiErrorResponse";
import {LotteryLicenceService} from "../../../services/lottery-licence.service";
import {ActiveUserService} from "../../../services/active-user.service";

@Component({
  selector: 'app-lottery-licence-edit',
  standalone: true,
  imports: [
    DialogBaseComponent,
    MatButton,
    MatDatepicker,
    MatDatepickerInput,
    MatDatepickerToggle,
    MatError,
    MatFormField,
    MatHint,
    MatInput,
    MatLabel,
    MatSuffix,
    NgIf,
    ReactiveFormsModule,
    MatSelect,
    FormsModule,
    MatOption
  ],
  templateUrl: './lottery-licence-edit.component.html',
  styleUrl: './lottery-licence-edit.component.css'
})
export class LotteryLicenceEditComponent {
  private lotteryLicenceService: LotteryLicenceService = inject(LotteryLicenceService);
  private datePipe: DatePipe = inject(DatePipe);
  private snackbarService: SnackbarService = inject(SnackbarService);
  private activeUserService: ActiveUserService = inject(ActiveUserService);

  public uiState: UIStateEnum = UIStateEnum.ShowData;
  public uiStateEnumForTemplate = UIStateEnum;

  licenceNumberControl = new FormControl<string | null>("", Validators.required);
  startDateControl = new FormControl<string | null>('', Validators.required);
  endDateControl = new FormControl<string | null>('', Validators.required);
  activeControl = new FormControl<boolean>(false);

  licenceForm = new FormGroup({
    licenceNumber: this.licenceNumberControl,
    startDate: this.startDateControl,
    endDate: this.endDateControl,
    active: this.activeControl
  });

  constructor(private matDialogRef: MatDialogRef<LotteryLicenceEditComponent>,
              @Inject(MAT_DIALOG_DATA) public lotteryLicence: IGameLotteryLicence) {
    this.populateForm(lotteryLicence);

    // Active/Inactive is derived from the licence's date window (and kept in step by the hourly sync
    // job), so it is shown but not hand-set.
    this.activeControl.disable();
    this.startDateControl.valueChanges.pipe(takeUntilDestroyed()).subscribe(() => this.refreshDerivedActive());
    this.endDateControl.valueChanges.pipe(takeUntilDestroyed()).subscribe(() => this.refreshDerivedActive());
  }

  refreshDerivedActive() {
    const start_day = this.toCalendarDay(this.startDateControl.value);
    const end_day = this.toCalendarDay(this.endDateControl.value);
    // The licence dates are UTC instants rendered as UTC calendar days, so today has to be the UTC calendar day too.
    const today = this.datePipe.transform(new Date(), 'yyyy-MM-dd', 'UTC');

    this.activeControl.setValue(!!start_day && start_day <= today! && (!end_day || end_day >= today!),
      {emitEvent: false});
  }

  // A licence window is whole days, so the comparison has to be day by day.
  private toCalendarDay(valueP: DateTime | string | Date | null): string | null {
    if (valueP instanceof Date) {
      return this.datePipe.transform(valueP, 'yyyy-MM-dd', 'UTC');
    }

    return toLicenceDate(valueP);
  }

  populateForm(lotteryLicenceP: IGameLotteryLicence) {
    this.licenceForm.setValue({
      licenceNumber: lotteryLicenceP.LicenceNumber,
      startDate: this.datePipe.transform(lotteryLicenceP.StartDate, 'yyyy-MM-ddTHH:mm:ss.SSS', 'UTC'),
      endDate: lotteryLicenceP.EndDate ? this.datePipe.transform(lotteryLicenceP.EndDate, 'yyyy-MM-ddTHH:mm:ss.SSS', 'UTC') : null,
      active: lotteryLicenceP.Active
    });

    this.refreshDerivedActive();
  }

  editLicenceNumber() {
    if (!this.licenceForm.valid) {
      this.licenceForm.markAllAsTouched();
      return;
    }

    this.lotteryLicenceService.editLotteryLicence({
      gameId: this.lotteryLicence.GameId,
      licenceNumber: this.licenceNumberControl.value!,
      endDate: toLicenceDate(this.endDateControl.value as DateTime | string | null)!,
      startDate: toLicenceDate(this.startDateControl.value as DateTime | string | null)!,
      id: this.lotteryLicence.Id,
      adminId: this.activeUserService.activeUser().Id
    }).subscribe({
      next: () => {
        this.matDialogRef.close();
        this.snackbarService.openSuccessfulSnackBar('Successfully edited licence number');
      }, error: (res: IApiErrorResponse) => {
        this.uiState = UIStateEnum.ShowData;
        if (res.Error?.ClientErrorMessages?.length > 0) {
          this.snackbarService.openErrorSnackBar(res.Error.ClientErrorMessages.toString());
        } else {
          this.snackbarService.openErrorSnackBar('There was an issue editing your licence number');
        }
      }
    })
  }
}
